import React, { useEffect, useState } from "react";

const dictVersion = '0';
const DICT_VERSION_STORAGE_KEY = "autoCompleteDictVersion";
const DICT_DB_NAME = "autoCompleteDictDB";
const DICT_STORE_NAME = "autoCompleteDictStore";
const DICT_DATA_KEY = "dict";

const openDictDatabase = () =>
	new Promise((resolve, reject) => {
		if (typeof window === "undefined" || !window.indexedDB) {
			resolve(null);
			return;
		}

		const request = window.indexedDB.open(DICT_DB_NAME, 1);

		request.onupgradeneeded = event => {
			const db = event.target.result;
			if (!db.objectStoreNames.contains(DICT_STORE_NAME)) {
				db.createObjectStore(DICT_STORE_NAME);
			}
		};

		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error);
	});

const getDictFromIndexedDB = async () => {
	try {
		const db = await openDictDatabase();
		if (!db) return [];

		return await new Promise((resolve, reject) => {
			const transaction = db.transaction(DICT_STORE_NAME, "readonly");
			const store = transaction.objectStore(DICT_STORE_NAME);
			const request = store.get(DICT_DATA_KEY);

			request.onsuccess = () => resolve(request.result || []);
			request.onerror = () => reject(request.error);
		});
	} catch (e) {
		return [];
	}
};

const saveDictToIndexedDB = dict =>
	new Promise(async resolve => {
		try {
			const db = await openDictDatabase();
			if (!db) {
				resolve();
				return;
			}

			const transaction = db.transaction(DICT_STORE_NAME, "readwrite");
			const store = transaction.objectStore(DICT_STORE_NAME);
			store.put(dict, DICT_DATA_KEY);

			transaction.oncomplete = () => resolve();
			transaction.onerror = () => resolve();
		} catch (e) {
			resolve();
		}
	});

export const useGetAutoCompleteDict = () => {
	const [dict, setDict] = useState([]);
	useEffect(() => {
		let isMounted = true;

		const loadDict = async () => {
			try {
				const storedVersion = localStorage.getItem(DICT_VERSION_STORAGE_KEY);
				if (storedVersion === dictVersion) {
					const cachedDict = await getDictFromIndexedDB();
					if (cachedDict && cachedDict.length) {
						if (isMounted) setDict(cachedDict);
						return;
					}
				}

				const dictModule = await import("../../../../dict.json");
				const latestDict = dictModule?.default || dictModule;

				await saveDictToIndexedDB(latestDict);
				localStorage.setItem(DICT_VERSION_STORAGE_KEY, dictVersion);

				if (isMounted) setDict(latestDict);
			} catch (e) {
				// Keep selector resilient if localStorage/IndexedDB/import fails.
			}
		};

		loadDict();

		return () => {
			isMounted = false;
		};
	},[])

	return dict;
}

export const withAutoCompleteDict = (WrappedComponent) => {
	return (props) => {
		const autoCompleteDict = useGetAutoCompleteDict();
	
		return (
		  <WrappedComponent 
			{...props} 
			autoCompleteDict={autoCompleteDict}
		  />
		);
	  };
}

